import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import * as Yup from 'yup';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import styles from './index.scss';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import MenuIcon from './icons/menu';
import {
  Formik,
  Form,
  FieldArray,
  FormikProps,
  useFormikContext,
  useField,
  FieldArrayRenderProps,
} from 'formik';
import List from './attachments/list';
import Fields from './attachments/fields';
import HelloModal from 'common/components/hello-modal';
import messages from './attachments/intl';
import HfReactHelper from 'common/utils/hf-react-helper';

interface InitialValues {
  attachments: types.Attachment[],
  current: types.Attachment,
}

type Props = {
  recipient: types.Signer | types.Recipient,
  index: number,
  showDeleteBtn: boolean,
  remove: () => void,
  showAuthFields: () => void,
};

type DropdownItemTypes = 'showAuthFields' | 'showAttachments' | 'removeSigner'

const autoGenPrefix = 'auto-gen-';
// New records are created automatically and have "auto-gen-" appended to their ID
const createAutoGenerated = (): types.Attachment => {
  const attachment = actions.generateAttachment();
  return {
    ...attachment,
    id: autoGenPrefix + attachment.id,
  };
};

const isAutoGenerated = ({ id }: types.Attachment) => id.includes(autoGenPrefix);

const RecipientDropdown = (props: Props) => {
  const parent = `recipients.${props.index}`;
  const intl = useIntl();
  const pricingUrl = HfReactHelper.urlHelper('info/pricing', 'hellosign');
  const allowRecipientAccessCode = useSelector(selectors.allowRecipientAccessCode);
  const canAccessRecipientAccessCodes = useSelector(selectors.canAccessRecipientAccessCodes);
  const allowRecipientSmsAuth = useSelector(selectors.allowRecipientSmsAuth);
  const showAuthenticationOption = allowRecipientAccessCode
    || canAccessRecipientAccessCodes
    || allowRecipientSmsAuth;
  const showAttachmentIcon = useSelector(selectors.showAttachmentIcon);
  const [, , attachmentsHelper] = useField<types.Attachment[]>(`${parent}.attachments`);
  const [, , authTypeHelpers] = useField<String>(`${parent}.authType`);
  const attachmentEnabled = useSelector(selectors.allowRecipientAttachment);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [upsellModalOpen, setUpsellModalOpen] = useState(false);

  const openAttachmentModal = () => setAttachmentModalOpen(true);
  const openUpsellModal = () => setUpsellModalOpen(true);
  const closeAttachmentModal = () => setAttachmentModalOpen(false);
  const closeUpsellModal = () => setUpsellModalOpen(false);

  const handleOpenModal = React.useCallback(() => {
    if (attachmentEnabled) {
      openAttachmentModal();
    } else {
      openUpsellModal();
    }
  }, [attachmentEnabled]);

  const handleCloseModal = () => {
    if (attachmentEnabled) {
      closeAttachmentModal();
    } else {
      closeUpsellModal();
    }
  };

  const onClose = (attachments: types.Attachment[]) => {
    attachmentsHelper.setValue(attachments);
  };

  const schema = Yup.object<InitialValues>({
    attachments: Yup.array().of(types.attachmentSchema)
      .unique(['name']),
    current: types.attachmentSchema,
  });

  const onSelection = React.useCallback((
    selection: DropdownItemTypes,
  ) => {
    if (selection === 'showAuthFields') {
      props.showAuthFields();
      authTypeHelpers.setValue('accessCode');
    } else if (selection === 'showAttachments') {
      handleOpenModal();
    } else if (selection === 'removeSigner') {
      props.remove();
    }
  }, [authTypeHelpers, handleOpenModal, props]);

  const initialValues: InitialValues = {
    attachments: 'attachments' in props.recipient ? props.recipient.attachments : [],
    current: createAutoGenerated(),
  };

  return (
    <div className={styles.recipientOptions}>
      <Dropdown.Wrapper
        className={styles.recipientDropdown}
        onSelection={onSelection}
      >
        <Dropdown.IconButton className={styles.iconButton}>
          <MenuIcon className={styles.menuIcon} />
        </Dropdown.IconButton>
        <Dropdown.Menu tag='ul' className={styles.recipientMenu}>
          {showAuthenticationOption && (
            <Dropdown.MenuItem
              tag='li'
              key='showAuthFields'
              value='showAuthFields'
              data-qa-ref='dropdown-item-add-signer-authentication'
            >
              <FormattedMessage
                id=""
                description="Enables fields for sender to set signer authentication before accessing the request"
                defaultMessage="Add signer authentication" />
            </Dropdown.MenuItem>
          )}
          {showAttachmentIcon && 'attachments' in props.recipient && (
            <Dropdown.MenuItem
              tag='li'
              key='showAttachments'
              value='showAttachments'
              data-qa-ref='dropdown-item-request-attachments'
            >
              <FormattedMessage
                id=""
                description="Enables fields for sender to request attachments from signer"
                defaultMessage="Request attachments" />
            </Dropdown.MenuItem>
          )}
          {props.showDeleteBtn && (
            <Dropdown.MenuItem
              tag='li'
              key='removeSigner'
              value='removeSigner'
              data-qa-ref='dropdown-item-remove-signer'
            >
              <FormattedMessage
                id=""
                description="Removes signer from signature request"
                defaultMessage="Remove signer" />
            </Dropdown.MenuItem>
          )}
        </Dropdown.Menu>
      </Dropdown.Wrapper>

      {attachmentModalOpen && (
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onClose(values.attachments);
            handleCloseModal();
          }}
          validateOnChange={false}
          validationSchema={schema}
          // eslint-disable-next-line react/no-children-prop
          children={({ handleChange }) => (
            <FieldArray name="attachments" render={(attachmentsHelpers) => (
              <AttachmentModal
                attachmentsHelpers={attachmentsHelpers}
                handleChange={handleChange} />
            )} />
          )} />
      )}
      {upsellModalOpen && (
        <HelloModal
          isOpen={true}
          onRequestClose={handleCloseModal}
          buttons={[
            {
              type: 'primary',
              text: intl.formatMessage(messages.modalUpsellAttachment),
              buttonLink: pricingUrl,
            },
          ]}
          contentLabel={intl.formatMessage(messages.modalUpsellAttachment)}
        >
          {intl.formatMessage(messages.modalBodyUpsell)}
        </HelloModal>
      )}
    </div>
  );
};

export default RecipientDropdown;

type AttachmentModalProps = {
  handleChange: FormikProps<InitialValues>['handleChange'],
  attachmentsHelpers: FieldArrayRenderProps,
}
function AttachmentModal({
  handleChange,
  attachmentsHelpers,
}: AttachmentModalProps) {
  const intl = useIntl();

  const { validateForm, handleSubmit } = useFormikContext();
  const [currentField, , currentHelper] = useField<types.Attachment>('current');
  const [attachmentsField] = useField<types.Attachment[]>('attachments');

  const isCurrentAutoGenerated = isAutoGenerated(currentField.value);

  const save = async () => {
    const hasErrors = await validateForm();

    if (Object.keys(hasErrors).length === 0) {
      // This was an auto-generated record, needs ID regenerated before adding to array
      if (isCurrentAutoGenerated) {
        const { id, ...payload } = currentField.value;
        attachmentsHelpers.push(actions.generateAttachment(payload));
      } else {
        const index = attachmentsField.value.findIndex(
          (attachment) => currentField.value.id === attachment.id,
        );
        attachmentsHelpers.replace(index, currentField.value);
      }

      currentHelper.setValue(createAutoGenerated());
      currentHelper.setTouched(false);
    }
  };

  const onClose = async () => {
    await save();

    currentHelper.setValue({
      ...createAutoGenerated(),
      // This needs a value that will pass validation.
      name: '_',
    });
    handleSubmit();
  };

  return (
    <HelloModal
      contentLabel={intl.formatMessage(messages.modalTitle)}
      width="540px"
      height="75vh"
      scrollable={true}
      isOpen={true}
      onRequestClose={onClose}
      buttons={[{
        type: 'primary',
        text: intl.formatMessage(messages.btnClose),
        'data-qa-ref': 'recipients.buttons.attachments.close',
        onClick: onClose,
      }]}
    >
      <Form
        onChange={(e) => {
          // Prevents parent's onChange from triggering,
          // which causes loss of field focus
          e.stopPropagation();
          return handleChange(e);
        }}
      >
        <p className="mb-3">
          {intl.formatMessage(messages.modalSubtext)}
        </p>

        <List
          createAutoGenerated={createAutoGenerated}
          formikHelpers={attachmentsHelpers} />

        <Fields
          save={save}
          isAutoGenerated={isCurrentAutoGenerated} />
      </Form>
    </HelloModal>

  );
}
